import { apiClient } from '../lib/apiClient';

/**
 * The signed-in user's licence (backend src/routes/license.routes.ts — no
 * licence required to call these):
 *   GET  /api/v1/licenses/me        → { data: OwnLicense | null }
 *   POST /api/v1/licenses/activate  { licenseCode }    → { data: OwnLicense }
 *   PUT  /api/v1/licenses/exchange  { newLicenseCode } → { data: { previousLicenseId, newLicense } }
 *
 * Error codes: INVALID_CODE / EMAIL_MISMATCH (400), ALREADY_USED /
 * USER_ALREADY_LICENSED (409), NO_ACTIVE_LICENSE (404), MISSING_SECRET (503).
 */

export type LicenseStatus = 'active' | 'expired' | 'revoked';

export interface OwnLicense {
  readonly id: string;
  /** licenses.plan: 'beta' (free beta), 'standard', 'reef', 'reef_pro', … */
  readonly plan: string;
  readonly status: LicenseStatus;
  readonly activatedAt: string;
  /** ISO timestamp; null = unlimited. */
  readonly expiresAt: string | null;
}

/** Days before expiry from which the licence page warns (finding #10). */
export const LICENSE_EXPIRY_WARNING_DAYS = 14;

export const ownLicenseQueryKey = ['license', 'me'] as const;

export async function fetchOwnLicense(): Promise<OwnLicense | null> {
  const res = await apiClient.get<{ data: OwnLicense | null }>('/api/v1/licenses/me');
  return res.data ?? null;
}

/**
 * Activates a key: a user without a valid licence activates, a user whose
 * licence is active exchanges it for the new key.
 */
export async function applyLicenseKey(licenseCode: string, hasActiveLicense: boolean): Promise<OwnLicense> {
  if (hasActiveLicense) {
    const res = await apiClient.put<{ data: { newLicense: OwnLicense } }>('/api/v1/licenses/exchange', {
      newLicenseCode: licenseCode,
    });
    return res.data.newLicense;
  }
  const res = await apiClient.post<{ data: OwnLicense }>('/api/v1/licenses/activate', { licenseCode });
  return res.data;
}

/** Whole days until `expiresAt` (rounded up; negative when past), or null when unlimited. */
export function daysUntil(expiresAt: string | null, now: Date = new Date()): number | null {
  if (expiresAt === null) return null;
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return null;
  return Math.ceil(ms / 86_400_000);
}
